/**
 * One next step under the call summary, in one of two states.
 *
 * UNBOUND: a checkbox the operator ticks, saved onto the call. Useful while
 * working a list, useless on Friday - the tick is on a call nobody reopens.
 *
 * BOUND: the step has been filed as a task. The checkbox then READS the task's
 * status instead of writing its own flag, and the row links to the task. Two
 * places holding "is this done" is two places that disagree the first time
 * somebody closes the task from the task list, so only one of them may own it.
 *
 * The action sits outside the `<label>` on purpose: a button inside a label
 * toggles the label's checkbox, which would file the task and tick the step in
 * one click.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { CallNextStep, CallStepTask } from "../../../lib/calls";
import { setTaskStatus } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";

/** What the quick pick offers, as days from today. `null` is "no date". */
const DUE_CHOICES: readonly { key: string; days: number | null }[] = [
  { key: "today", days: 0 },
  { key: "tomorrow", days: 1 },
  { key: "week", days: 7 },
  { key: "none", days: null },
];

/** End of the chosen day, so a task due today is not overdue this afternoon. */
function dueAt(days: number | null): string | null {
  if (days === null) return null;
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(23, 59, 0, 0);
  return d.toISOString();
}

export function CallStepRow({
  step,
  task,
  canMakeTask,
  onToggle,
  onTask,
}: {
  step: CallNextStep;
  /** The task this step was filed as. Undefined when unbound, or deleted. */
  task: CallStepTask | undefined;
  canMakeTask: boolean;
  onToggle: (text: string) => Promise<void>;
  onTask: (text: string, dueDate: string | null) => Promise<void>;
}) {
  const t = useT();
  const close = useClosePops();
  const [busy, setBusy] = useState(false);
  // Optimistic, the same trade the tick itself makes: the cheap failure is a box
  // that flicks back, the expensive one is a box that sits still and gets
  // clicked twice.
  const [taskDone, setTaskDone] = useState<boolean | null>(null);

  const bound = !!step.taskId;
  // A binding whose task is gone falls back to the stored flag rather than
  // freezing: the task list is where it was closed, and the step should not be
  // stuck on a state nobody can change from either side.
  const orphaned = bound && !task;
  const done = task ? (taskDone ?? task.status === "done") : step.done;

  async function toggleTask() {
    if (!task) return;
    const next = !done;
    setTaskDone(next);
    try {
      await setTaskStatus(task.id, next ? "done" : "todo");
    } catch {
      setTaskDone(!next);
    }
  }

  async function file(days: number | null) {
    close();
    setBusy(true);
    try {
      await onTask(step.text, dueAt(days));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="crm-step-row">
      <label className="crm-step">
        <input
          type="checkbox"
          checked={done}
          onChange={() => (task ? void toggleTask() : void onToggle(step.text))}
          className="crm-step-b"
        />
        <span className={done ? "crm-step-x" : undefined}>{step.text}</span>
      </label>

      {task ? (
        <Link className="crm-step-task" to={`/tasks/${task.id}`} title={t("calls.detail.taskOpen")}>
          <CheckSquare className="size-3.5" />
          {t("calls.detail.taskOpen")}
        </Link>
      ) : orphaned ? (
        <span className="crm-step-task is-gone" title={t("calls.detail.taskGone")}>
          {t("calls.detail.taskGone")}
        </span>
      ) : canMakeTask && !done ? (
        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={cn("crm-step-task", open && "is-on")}
              title={t("calls.detail.makeTask")}
              aria-expanded={open}
              disabled={busy}
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <CheckSquare className="size-3.5" />}
              {t("calls.detail.makeTask")}
            </button>
          )}
        >
          <div className="crm-menu-head">
            <div className="crm-menu-t">{t("calls.detail.makeTask")}</div>
            <div className="crm-menu-s">{t("calls.detail.taskDue")}</div>
          </div>
          {DUE_CHOICES.map((choice) => (
            <button key={choice.key} type="button" className="crm-mi" onClick={() => void file(choice.days)}>
              {t(`calls.detail.taskDue_${choice.key}`)}
            </button>
          ))}
        </Pop>
      ) : null}
    </div>
  );
}
